/**
 * Namensnormalisierung für den OCR-Spielerabgleich.
 *
 * 2K zeigt Namen in Originalschreibweise ("L. Dončić", "K. Matković",
 * "H. González"), in der Datenbank stehen sie überwiegend transliteriert
 * ("Doncic", "Matkovic", "Gonzalez") — vereinzelt aber auch mit Diakritika
 * ("Marković") oder mit kaputten Zeichen aus Alt-Importen ("O�Neale").
 * Ein reines toLowerCase() lässt diese Namen deshalb durchfallen. Verglichen
 * wird darum immer die normalisierte Form, und zwar auf beiden Seiten.
 */

// Buchstaben, die die Unicode-Zerlegung (NFD) nicht in Grundbuchstabe +
// kombinierendes Zeichen zerlegt — die müssen von Hand abgebildet werden.
const SPECIAL_LETTERS = {
  'đ': 'd', 'Đ': 'd',
  'ø': 'o', 'Ø': 'o',
  'ł': 'l', 'Ł': 'l',
  'ß': 'ss',
  'æ': 'ae', 'Æ': 'ae',
  'œ': 'oe', 'Œ': 'oe',
  'þ': 'th', 'Þ': 'th',
  'ð': 'd', 'Ð': 'd',
  'ı': 'i', 'İ': 'i',
  'ħ': 'h', 'Ħ': 'h'
};

/**
 * Bringt einen Namen auf eine vergleichbare Form: ohne Akzente, klein,
 * ohne Punkte, Apostrophe, Bindestriche und Ersatzzeichen.
 * @param {string} value
 * @returns {string}
 */
export const normalizeName = (value) => {
  if (!value) return '';

  return String(value)
    .replace(/[đĐøØłŁßæÆœŒþÞðÐıİħĦ]/g, (ch) => SPECIAL_LETTERS[ch])
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // kombinierende Akzente entfernen
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, '') // Punkt, Apostroph, Bindestrich, U+FFFD
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Normalisierter Gesamtname eines DB-Spielers.
 * @param {{firstName: string, lastName: string}} player
 * @returns {string}
 */
export const normalizeFullName = (player) =>
  normalizeName(`${player.firstName} ${player.lastName}`);

/**
 * Namenserkennung für die textbasierten Parser.
 *
 * 2K schreibt Namen in Originalschreibweise, also mit Diakritika ("Dončić",
 * "Šengün"), dazu kommen Nachnamen mit Großbuchstaben in der Mitte
 * ("McBride", "LaRavia"), mit Apostroph ("O'Neale") und mit Punkt ("Jr.").
 * Ein Muster wie [A-Z][a-z]+ lässt all das durchfallen — und eine Zeile, die
 * nicht als Name erkannt wird, verschwindet lautlos aus dem Ergebnis. Statt
 * die Zeichenklassen immer weiter aufzublähen, wird deshalb gegen die
 * normalisierte Form geprüft: dort ist von "Dončić" nur noch "doncic" übrig.
 */

/** Beginnt der Text mit einem Großbuchstaben (auch mit Diakritikum)? */
const startsUpper = (text) => {
  const first = String(text || '').charAt(0);
  return !!first && first !== first.toLowerCase() && first === first.toUpperCase();
};

/** Durchgehend groß geschrieben — Spaltenköpfe wie "MIN", "PTS", "DNP". */
const isAllCaps = (text) => {
  const s = String(text || '');
  return s === s.toUpperCase() && s !== s.toLowerCase();
};

/**
 * Ein Vorname-Initial als eigenes Wort: "J.", "P.J.".
 * @param {string} text
 * @returns {boolean}
 */
export const isInitialToken = (text) => {
  const s = String(text || '').trim();
  if (!/^.\.(?:.\.)?$/.test(s) || !startsUpper(s)) return false;
  return /^[a-z]{1,2}$/.test(normalizeName(s));
};

/**
 * Ein Nachname als eigenes Wort: "Brunson", "Dončić", "McBride", "O'Neale",
 * "LaRavia", "Jr.".
 * @param {string} text
 * @returns {boolean}
 */
export const isLastNameToken = (text) => {
  const s = String(text || '').trim();
  if (s.length < 2 || !startsUpper(s) || isAllCaps(s)) return false;
  return /^[a-z]+$/.test(normalizeName(s));
};

/**
 * Beginnt die Zeile mit "Initial. Nachname"? ("L. Dončić 17 11")
 * @param {string} line
 * @returns {boolean}
 */
export const startsWithPlayerName = (line) => {
  const words = String(line || '').trim().split(/\s+/);
  if (words.length < 2) return false;
  return isInitialToken(words[0]) && isLastNameToken(words[1]);
};

/**
 * Lockerer Zeilenanfang — auch ohne Punkt und ohne zweites Wort:
 * "F Wagner", "J.Brunson", "Dončić".
 * @param {string} line
 * @returns {boolean}
 */
export const looksLikePlayerLine = (line) => {
  const first = String(line || '').trim().split(/\s+/)[0] || '';
  if (!startsUpper(first)) return false;
  // "J.Brunson" kommt als ein Wort an — Punkte zählen nicht als Trenner,
  // "F Wagner" dagegen als blankes Initial ohne Punkt.
  return /^[a-z]+$/.test(normalizeName(first)) || isInitialToken(first);
};

/**
 * Namenszusaetze, die 2K und die Datenbank hinten anhaengen. Sie gehoeren
 * nicht zum Nachnamen: wer sie mitzaehlt, vergleicht "jr" gegen "portis".
 */
const NAME_SUFFIXES = ['jr', 'sr', 'ii', 'iii', 'iv'];

/**
 * Zerlegt einen Namen in Vorname (oder Initial), Nachname und Zusatz.
 *
 * "B. Portis Jr."    -> { first: 'b',      last: 'portis',    suffix: 'jr' }
 * "Bobby Portis Jr." -> { first: 'bobby',  last: 'portis',    suffix: 'jr' }
 * "S. De Larrea"     -> { first: 's',      last: 'de larrea', suffix: ''   }
 * "Antetokounmpo"    -> { first: '',       last: 'antetokounmpo', suffix: '' }
 *
 * Alles zwischen Vorname und Zusatz zaehlt zum Nachnamen — mehrteilige
 * Nachnamen wie "De Larrea" bleiben so zusammen.
 *
 * @param {string} value
 * @returns {{first: string, last: string, suffix: string}}
 */
export const splitPlayerName = (value) => {
  const words = normalizeName(value).split(' ').filter(Boolean);
  const suffixes = [];

  while (words.length > 1 && NAME_SUFFIXES.includes(words[words.length - 1])) {
    suffixes.unshift(words.pop());
  }

  if (words.length === 0) return { first: '', last: '', suffix: suffixes.join(' ') };

  return {
    first: words.length > 1 ? words[0] : '',
    last: words.length > 1 ? words.slice(1).join(' ') : words[0],
    suffix: suffixes.join(' ')
  };
};

/** Zerlegter Name eines Datenbank-Spielers. */
export const splitDbName = (player) =>
  splitPlayerName(`${player.firstName} ${player.lastName}`);

/**
 * Die Summenzeile am Tabellenende ("Ges.", "Total"), die 2K unter die Spieler
 * setzt. Sie sieht fuer die OCR aus wie eine Spielerzeile, gehoert aber nicht
 * in den Boxscore.
 * @param {string} name
 * @returns {boolean}
 */
export const isTotalsRow = (name) => {
  const n = normalizeName(name);
  return n === 'ges' || n === 'gesamt' || n === 'total' || n === 'totals' || n === 'team';
};

/**
 * Levenshtein-Distanz, abgebrochen sobald das Limit überschritten ist.
 * @param {string} a
 * @param {string} b
 * @param {number} limit
 * @returns {number} Distanz, oder limit + 1 wenn darüber
 */
const editDistance = (a, b, limit) => {
  if (Math.abs(a.length - b.length) > limit) return limit + 1;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (row[j] < best) best = row[j];
    }
    if (best > limit) return limit + 1;
    prev = row;
  }

  return prev[b.length];
};

/**
 * Nachnamen, die sich um höchstens einen Buchstaben unterscheiden — fängt
 * einzelne OCR-Verleser ab ("Matkovlc" statt "Matkovic"). Erst ab fünf
 * Buchstaben, sonst träfe es zu viele kurze Namen gleichzeitig.
 * @param {string} a normalisierter Nachname
 * @param {string} b normalisierter Nachname
 * @returns {boolean}
 */
export const isNearLastName = (a, b) => {
  if (!a || !b || a === b) return a === b && !!a;
  if (a.length < 5 || b.length < 5) return false;
  return editDistance(a, b, 1) <= 1;
};

const nameNormalize = {
  normalizeName,
  normalizeFullName,
  isNearLastName,
  splitPlayerName,
  splitDbName,
  isTotalsRow,
  isInitialToken,
  isLastNameToken,
  startsWithPlayerName,
  looksLikePlayerLine
};

export default nameNormalize;
